import React, { useState, useEffect } from 'react';
import { Loader2, Copy, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from '@/hooks/use-toast';
import deviceService from '@/services/deviceService';
import websocketService from '@/services/websocketService';
import { MainLayout } from '@/components/layout/MainLayout';
import Player from '@/components/Player';
import { localStateStore } from '@/utils/localStateStore';

const BACKDROP_ARTWORKS = [
  'https://data.cloudmedia.com.tr/artworks/1739378080375-4632609-AdsA_z_tasarA_m__4_.jpg',
  'https://data.cloudmedia.com.tr/artworks/1740591151739-860020399-bossa.jpg',
  'https://data.cloudmedia.com.tr/artworks/1741366685978-352999401-Slap_House.jpg',
  'https://data.cloudmedia.com.tr/artworks/1742026381554-750740315-Pop_hit.jpg',
  'https://data.cloudmedia.com.tr/artworks/1739398136588-879331812-Album_Cover_Instagram_Post.jpg',
  'https://data.cloudmedia.com.tr/artworks/1740494467109-60463038-Yellow_and_Black_Retro_Music_Album_Cover.jpg',
];

type DeviceStatus = 'unregistered' | 'registered' | 'waiting_playlist' | 'connected';

interface Playlist {
  _id: string;
  name: string;
  songs: Array<{
    _id: string;
    title: string;
    name: string;
    artist: string;
    filePath: string;
    duration: number;
    cdnUrl?: string;
    artwork?: string;
  }>;
  artwork?: string;
  baseUrl?: string;
}

const TokenVerification: React.FC = () => {
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [buttonLoading, setButtonLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [status, setStatus] = useState<DeviceStatus>('unregistered');
  const [initialLoading, setInitialLoading] = useState(true);
  const [playlist, setPlaylist] = useState<Playlist | null>(null);
  const [backdropIndex, setBackdropIndex] = useState(0);

  useEffect(() => {
    if (status !== 'connected') return;
    const id = setInterval(() => {
      setBackdropIndex(i => (i + 1) % BACKDROP_ARTWORKS.length);
    }, 6000);
    return () => clearInterval(id);
  }, [status]);

  // Window boyutları - Landscape kontrolü için
  const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1024);
  const [windowHeight, setWindowHeight] = useState(typeof window !== 'undefined' ? window.innerHeight : 768);
  const isLandscape = windowWidth > windowHeight && windowHeight < 500;

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
      setWindowHeight(window.innerHeight);
    };
    const handleOrientationChange = () => {
      setTimeout(handleResize, 100);
    };

    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleOrientationChange);
    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleOrientationChange);
    };
  }, []);

  // Başlangıç kontrolü - Token durumu
  useEffect(() => {
    const checkInitialState = async () => {
      const storedToken = deviceService.getStoredToken();
      const storedPlaylist = localStateStore.getJson<Playlist>('currentPlaylist', null);

      if (storedToken) {
        setToken(storedToken);
        setStatus('registered');
      }

      // Eğer kaydedilmiş playlist varsa, direkt player'ı göster
      if (storedPlaylist) {
        setPlaylist(storedPlaylist);
      }
      
      setTimeout(() => setInitialLoading(false), 500);
    };

    checkInitialState();
  }, []);

  // Playlist event listener
  useEffect(() => {
    const handlePlaylistReceived = (event: CustomEvent) => {
      console.log('Playlist alındı:', event.detail);
      setPlaylist(event.detail);
    };

    const handleSchedulePlaylistActivated = (event: CustomEvent) => {
      if (event.detail?.playlist) {
        setPlaylist(event.detail.playlist);
        setStatus('connected');
      }
    };

    const handleSwitchToManualPlaylist = (event: CustomEvent) => {
      if (event.detail?.playlist) {
        setPlaylist(event.detail.playlist);
        setStatus('connected');
      }
    };
    
    const handlePlaylistRemoved = () => {
      console.log('Playlist kaldırıldı');
      setPlaylist(null);
      setStatus('waiting_playlist');
    };

    window.addEventListener('playlistReceived', handlePlaylistReceived as EventListener);
    window.addEventListener('playlistRemoved', handlePlaylistRemoved as EventListener);
    window.addEventListener('schedule-playlist-activated', handleSchedulePlaylistActivated as EventListener);
    window.addEventListener('switch-to-manual-playlist', handleSwitchToManualPlaylist as EventListener);

    return () => {
      window.removeEventListener('playlistReceived', handlePlaylistReceived as EventListener);
      window.removeEventListener('playlistRemoved', handlePlaylistRemoved as EventListener);
      window.removeEventListener('schedule-playlist-activated', handleSchedulePlaylistActivated as EventListener);
      window.removeEventListener('switch-to-manual-playlist', handleSwitchToManualPlaylist as EventListener);
    };
  }, []);

  // Auth mesaj dinleyicisi
  useEffect(() => {
    const authHandler = (message: any) => {
      if (message.success) {
        setStatus('connected');
        
        if (!localStateStore.getString('initialConnectionDone')) {
          toast({
            title: "Cihaz Bağlandı",
            description: "Token doğrulandı, cihaz aktif.",
          });
          localStateStore.setString('initialConnectionDone', 'true');
        }
      }
    };

    websocketService.addMessageHandler('auth', authHandler);

    return () => {
      websocketService.removeMessageHandler('auth', authHandler);
    };
  }, [token]);

  // Token oluşturma
  useEffect(() => {
    const initializeToken = async () => {
      const storedToken = deviceService.getStoredToken();
      
      if (storedToken) {
        setToken(storedToken);
        setStatus('registered');
        websocketService.sendAuth(storedToken);
      } else {
        try {
          setIsLoading(true);
          const newToken = await deviceService.registerDevice();
          setToken(newToken);
          setStatus('registered');
          
          
          websocketService.sendAuth(newToken);
        } catch (err) {
          toast({
            variant: "destructive",
            title: "Hata",
            description: "Token oluşturulurken bir hata oluştu.",
          });
        } finally {
          setIsLoading(false);
        }
      }
    };

    if (!token) {
      initializeToken();
    }
  }, [token]);

  const generateNewToken = async () => {
    setButtonLoading(true);
    try {
      const newToken = await deviceService.registerDevice();
      setToken(newToken);
      setStatus('registered');
      

      websocketService.sendAuth(newToken);
    } catch (err) {
      toast({
        variant: "destructive",
        title: "Hata",
        description: "Token oluşturulurken bir hata oluştu.",
      });
    } finally {
      setButtonLoading(false);
    }
  };

  const copyToClipboard = () => {
    if ('clipboard' in navigator && navigator.clipboard && token) {
      (navigator.clipboard as Clipboard).writeText(token);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000); // 2 saniye sonra normale dön
    }
  };

  const getStatusText = () => {
    switch (status) {
      case 'connected':
        return '';
      case 'waiting_playlist':
        return 'Playlist Bekleniyor';
      case 'registered':
        return '';
      default:
        return 'Token oluşturuluyor';
    }
  };

  const getStatusDescription = () => {
    switch (status) {
      case 'connected':
        return 'Cihaz başarıyla eklendi. Admin panelinden playlist atayabilirsiniz.';
      case 'waiting_playlist':
        return 'Admin panelinden playlist atayabilirsiniz';
      case 'registered':
        return 'Lütfen token kodunu cihaz formuna ekleyin.';
      default:
        return 'Lütfen bekleyin...';
    }
  };

  // Eğer playlist varsa Player'ı göster
  if (playlist) {
    return <Player playlist={playlist} />;
  }

  // Landscape Layout - Dikey tasarıma benzer, yatay kompakt
  if (isLandscape) {
    return (
      <MainLayout isLoading={initialLoading}>
        <div
          className="fixed inset-0 flex items-center justify-center"
          style={{
            backgroundColor: '#ffed00',
            paddingTop: 'env(safe-area-inset-top)',
            paddingBottom: 'env(safe-area-inset-bottom)',
            paddingLeft: 'env(safe-area-inset-left)',
            paddingRight: 'env(safe-area-inset-right)'
          }}
        >
          {/* Siyah kart - yatay layout */}
          <div className="bg-black rounded-2xl px-8 py-5 flex items-center gap-8">
            {/* Sol: Logo */}
            <div className="flex flex-col items-center">
              <span className="font-bold text-2xl text-[#ffed00]" style={{ letterSpacing: '-1px' }}>veeq</span>
              <span className="text-white text-xs mt-1">Web Player</span>
            </div>

            {/* Orta: Token kutusu */}
            <div className="flex flex-col items-center gap-2">
              <div
                className="text-2xl font-bold tracking-wider text-black py-2 px-5 rounded-lg"
                style={{ backgroundColor: '#ffed00' }}
              >
                {token || '...'}
              </div>
              <p className="text-gray-400 text-xs">{getStatusDescription()}</p>
            </div>

            {/* Sağ: Butonlar */}
            {token && (
              <div className="flex flex-col gap-2">
                <Button
                  onClick={copyToClipboard}
                  size="sm"
                  className="bg-gray-700 hover:bg-gray-600 text-white text-xs h-8"
                >
                  {copied ? (
                    <CheckCircle className="w-3 h-3 mr-1 text-[#ffed00]" />
                  ) : (
                    <Copy className="w-3 h-3 mr-1" />
                  )}
                  Kopyala
                </Button>
                <Button
                  onClick={generateNewToken}
                  size="sm"
                  className="bg-[#ffed00] hover:bg-[#e6d600] text-black font-bold text-xs h-8"
                  disabled={buttonLoading}
                >
                  {buttonLoading ? (
                    <Loader2 className="w-3 h-3 animate-spin" />
                  ) : (
                    'Yeni Token'
                  )}
                </Button>
              </div>
            )}
          </div>
        </div>
      </MainLayout>
    );
  }

  const setupSteps = status === 'connected'
    ? [
        { num: '1', text: 'Admin panelde "Playlistler"e gidin' },
        { num: '2', text: 'Bir playlist seçin' },
        { num: '3', text: '"Cihaza Ata" ile bu cihazı seçin' },
      ]
    : [
        { num: '1', text: 'Admin panele giriş yapın' },
        { num: '2', text: 'Cihazlar sekmesinden "Yeni Cihaz" ekleyin' },
        { num: '3', text: 'Aşağıdaki token kodunu yapıştırın' },
      ];

  // Portrait Layout - Normal dikey tasarım
  return (
    <MainLayout isLoading={initialLoading}>
      <div className="relative flex flex-col items-center justify-center min-h-screen p-4 gap-6 overflow-hidden" style={{ backgroundColor: '#ffed00' }}>
        {status === 'connected' && (
          <div className="pointer-events-none absolute inset-0 z-0" aria-hidden="true">
            {BACKDROP_ARTWORKS.map((src, i) => (
              <img
                key={i}
                src={src}
                alt=""
                className="absolute inset-0 w-full h-full object-cover transition-opacity ease-in-out"
                style={{
                  transitionDuration: '1500ms',
                  opacity: i === backdropIndex ? 1 : 0,
                  filter: 'blur(10px) brightness(0.85)',
                  transform: 'scale(1.08)',
                  animation: i === backdropIndex ? 'backdropZoom 12s ease-out forwards' : 'none',
                }}
              />
            ))}
            <div className="absolute inset-0" style={{ background: 'radial-gradient(ellipse at center, rgba(0,0,0,0.35) 0%, rgba(0,0,0,0.15) 60%, rgba(0,0,0,0) 100%)' }} />
            <style>{`
              @keyframes backdropZoom {
                from { transform: scale(1.0); }
                to   { transform: scale(1.04); }
              }
            `}</style>
          </div>
        )}
        <div className={`w-full max-w-[420px] mx-4 relative z-10 ${status === 'connected' ? 'bg-black rounded-2xl px-5 py-4' : ''}`}>
          <div className="relative flex items-start justify-between gap-3">
            <div className={`absolute top-4 left-[16.67%] right-[16.67%] h-px ${status === 'connected' ? 'bg-white/25' : 'bg-black/30'}`} aria-hidden="true" />
            {setupSteps.map((step) => (
              <div key={step.num} className="relative flex-1 flex flex-col items-center text-center">
                <div className={`w-8 h-8 rounded-full font-bold flex items-center justify-center mb-2 text-sm shadow-sm ${status === 'connected' ? 'bg-[#ffed00] text-black' : 'bg-black text-[#ffed00]'}`}>
                  {step.num}
                </div>
                <p className={`text-[11px] sm:text-xs font-medium leading-tight ${status === 'connected' ? 'text-white' : 'text-black'}`}>{step.text}</p>
              </div>
            ))}
          </div>
        </div>
        <Card className="w-full max-w-[420px] mx-4 bg-black backdrop-blur-sm border border-black relative z-10">
          {status === 'connected' ? (
            <div className="flex flex-col items-center justify-center py-16 sm:py-20 px-6">
              <div className="font-bold text-3xl sm:text-4xl md:text-5xl text-[#ffed00]" style={{ letterSpacing: '-1px' }}>
                {['v', 'e', 'e', 'q'].map((letter, index) => (
                  <span
                    key={index}
                    style={{
                      animation: `letterFade 1.8s infinite ${index * 0.2}s`,
                      display: 'inline-block'
                    }}
                  >
                    {letter}
                  </span>
                ))}
                <style>{`
                  @keyframes letterFade {
                    0%, 100% { opacity: 1; transform: translateY(0); }
                    25% { opacity: 0.3; transform: translateY(-8px); }
                    50% { opacity: 1; transform: translateY(0); }
                  }
                `}</style>
              </div>
              <p className="text-white text-xs sm:text-sm mt-6 font-medium">Playlist bekleniyor...</p>
            </div>
          ) : (
          <>
          <CardHeader className="space-y-2 sm:space-y-3 flex flex-col items-center py-6 sm:py-8">
            <div className="mb-3 sm:mb-4">
              <span className="font-bold text-3xl sm:text-4xl md:text-5xl text-[#ffed00]" style={{ letterSpacing: '-1px' }}>
                veeq
              </span>
            </div>
            <CardTitle className="text-xl sm:text-2xl text-white text-center">Web Player</CardTitle>
            <CardDescription className="text-gray-400 text-center text-sm sm:text-base">
              {getStatusText()}
            </CardDescription>
          </CardHeader>
          <CardContent className="px-4 pb-6 sm:px-8 sm:pb-8">
            <div className="space-y-6 sm:space-y-8">
              {isLoading ? (
                <div className="flex justify-center">
                  <Loader2 className="w-8 h-8 animate-spin text-indigo-500" />
                </div>
              ) : token ? (
                <div className="flex flex-col items-center space-y-6">
                  <div className="text-white text-center mb-2">
                    <p className="text-sm sm:text-base">{getStatusDescription()}</p>
                  </div>
                  <div className="text-2xl sm:text-3xl md:text-4xl font-bold tracking-wider text-black py-3 px-6 sm:py-4 sm:px-8 rounded-lg" style={{ backgroundColor: '#ffed00' }}>
                    {token}
                  </div>
                  <div className="flex flex-col sm:flex-row gap-2 sm:space-x-2 w-full sm:w-auto">
                    <Button
                      onClick={copyToClipboard}
                      className="bg-gray-700 hover:bg-gray-700 text-xs sm:text-sm"
                    >
                      {copied ? (
                        <CheckCircle className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2 text-[#ffed00]" />
                      ) : (
                        <Copy className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                      )}
                      Kopyala
                    </Button>
                    <Button
                      onClick={generateNewToken}
                      className="bg-[#ffed00] hover:bg-[#ffed00] text-black font-bold text-xs sm:text-sm"
                      disabled={buttonLoading}
                    >
                      {buttonLoading ? (
                        <>
                          <Loader2 className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2 animate-spin" />
                          Oluşturuluyor
                        </>
                      ) : (
                        'Yeni Token Oluştur'
                      )}
                    </Button>
                  </div>
                </div>
              ) : null}
            </div>
          </CardContent>
          </>
          )}
        </Card>
      </div>
    </MainLayout>
  );
};

export default TokenVerification;
